import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ProviderInsightsSubAreaLabels } from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { ProviderInsightsSubArea } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'

import { Display } from '../../atoms/display/Display'
import { ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { EyeIcon } from '../../atoms/icons/EyeIcon'
import { InsightsMetricCard, InsightsMetricCardProps } from '../../atoms/insightsMetricCard/InsightsMetricCard'
import { Link } from '../../atoms/link/Link'
import {
  SKILLS_PROGRESS_EFFECTIVE_THRESHOLD,
  SKILLS_PROGRESS_EXCEPTIONAL_THRESHOLD,
  SupervisorSkillsCardRow,
} from '../../atoms/supervisorSkillsCardRow/SupervisorSkillsCardRow'
import { Title } from '../../atoms/title/Title'
import { BodyTextSize, DisplaySize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface SupervisorSkillsCardProps {
  currentMonthSubAreas?: ProviderInsightsSubArea[]
  previousMonthSubAreas?: ProviderInsightsSubArea[]
  onUsageGuidelinesPress?: () => void
  isLoadingProviderInsights?: boolean
  supervisorMetrics?: InsightsMetricCardProps[]
  isLoadingCareInsightsMetrics?: boolean
  supervisorMetricsPerRow?: number
  sectionAverageScore?: number | string
}

const CardContainer = styled.View(({ theme }) => ({
  borderRadius: '8px',
  borderWidth: '1px',
  borderColor: theme.colors.components.supervisorSkillsCard.border,
  overflow: 'hidden',
}))

const HeaderContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.colors.components.supervisorSkillsCard.header.background,
  padding: `${theme.spacing['8px']} ${theme.spacing['16px']}`,
}))

const HeaderLeftContent = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const BodyContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['16px'],
  backgroundColor: theme.colors.components.supervisorSkillsCard.background,
  gap: theme.spacing['24px'],
}))

const SkillsSectionContainer = styled.View<{ borderLeftColor: string }>(({ theme, borderLeftColor }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  borderLeftWidth: 2,
  borderLeftColor,
  paddingLeft: theme.spacing['16px'],
}))

const SkillsSectionInfoContainer = styled.View({
  flex: 1,
  gap: 8,
})

const MetricsContainer = styled.View(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  flexWrap: 'wrap',
  gap: theme.spacing['16px'],
}))

export const SupervisorSkillsCard: React.FC<SupervisorSkillsCardProps> = ({
  currentMonthSubAreas = [],
  previousMonthSubAreas,
  onUsageGuidelinesPress,
  isLoadingProviderInsights = false,
  supervisorMetrics = [],
  supervisorMetricsPerRow = 2,
  isLoadingCareInsightsMetrics = false,
  sectionAverageScore,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const componentColors = colors.components.supervisorSkillsCard

  const sectionAverageScoreText = useMemo(() => {
    if (sectionAverageScore === undefined) {
      return undefined
    }
    if (Number(sectionAverageScore) >= SKILLS_PROGRESS_EXCEPTIONAL_THRESHOLD) {
      return 'Exceptional'
    }
    if (Number(sectionAverageScore) >= SKILLS_PROGRESS_EFFECTIVE_THRESHOLD) {
      return 'Effective'
    }
    return 'Opportunity'
  }, [sectionAverageScore])

  const rankedSkillsWithProgress = useMemo(() => {
    const currentMonthSubAreaRatings = currentMonthSubAreas
      .filter((subArea) => subArea.subAreaName in ProviderInsightsSubAreaLabels)
      .map((subArea) => {
        const previousMonthSubArea = previousMonthSubAreas?.find((sa) => sa.subAreaName === subArea.subAreaName)
        const progress = previousMonthSubArea
          ? Number(subArea.averageScore.value) - Number(previousMonthSubArea.averageScore.value)
          : undefined
        const arrowDirection =
          progress && Math.abs(progress) >= 0.05
            ? progress > 0
              ? ArrowIconDirection.UPRIGHT
              : ArrowIconDirection.DOWNRIGHT
            : undefined
        return {
          value: subArea.subAreaName,
          label: ProviderInsightsSubAreaLabels[subArea.subAreaName],
          averageScore: Number(subArea.averageScore.value),
          ...(arrowDirection && { arrowDirection, progress }),
        }
      })
      .sort((a, b) => b.averageScore - a.averageScore)
    return currentMonthSubAreaRatings
  }, [currentMonthSubAreas, previousMonthSubAreas])

  // Top 3 skills go in highest-rated skills and bottom 3 go in lowest-rated skills, if there
  // are fewer than 6 skills, put exceptional and effective ones in highest, rest in lowest
  const { highestRatedSkillsWithProgress, lowestRatedSkillsWithProgress } = useMemo(() => {
    const totalSkills = rankedSkillsWithProgress.length

    if (totalSkills >= 6) {
      return {
        highestRatedSkillsWithProgress: rankedSkillsWithProgress.slice(0, 3),
        lowestRatedSkillsWithProgress: rankedSkillsWithProgress.slice(-3),
      }
    }

    const exceptionalAndEffective = rankedSkillsWithProgress.filter(
      (skill) => skill.averageScore >= SKILLS_PROGRESS_EFFECTIVE_THRESHOLD,
    )

    if (exceptionalAndEffective.length <= 3) {
      const remaining = rankedSkillsWithProgress.filter(
        (skill) => skill.averageScore < SKILLS_PROGRESS_EFFECTIVE_THRESHOLD,
      )
      return {
        highestRatedSkillsWithProgress: exceptionalAndEffective,
        lowestRatedSkillsWithProgress: remaining,
      }
    }

    return {
      highestRatedSkillsWithProgress: rankedSkillsWithProgress.slice(0, 3),
      lowestRatedSkillsWithProgress: rankedSkillsWithProgress.slice(3),
    }
  }, [rankedSkillsWithProgress])

  return (
    <CardContainer testID={tID('SupervisorSkillsCard')}>
      <HeaderContainer>
        <HeaderLeftContent>
          <EyeIcon size={24} color={componentColors.header.text} isOpen />
          <Display
            text={formatMessage({
              defaultMessage: 'Visible only to supervisors',
              description: 'Label indicating content is only visible to supervisors',
            })}
            size={DisplaySize.EYEBROW}
            color={componentColors.header.text}
            testID={tID('SupervisorSkillsCard-visibleOnlyToSupervisors')}
          />
        </HeaderLeftContent>
        {onUsageGuidelinesPress && (
          <Link
            text={formatMessage({
              defaultMessage: 'Usage guidelines',
              description: 'Link to usage guidelines',
            })}
            underline
            onPress={onUsageGuidelinesPress}
            size={BodyTextSize.SMALL}
            testID={tID('SupervisorSkillsCard-usageGuidelinesLink')}
          />
        )}
      </HeaderContainer>
      <BodyContainer>
        {sectionAverageScoreText && (
          <Title
            text={sectionAverageScoreText}
            size={TitleSize.SMALL}
            color={colors.textPrimary}
            testID={tID('SupervisorSkillsCard-sectionAverageScore')}
          />
        )}
        {highestRatedSkillsWithProgress.length > 0 && (
          <SkillsSectionContainer
            borderLeftColor={componentColors.successSection.border}
            testID={tID('SupervisorSkillsCard-highestRatedSkillsSection')}
          >
            <SkillsSectionInfoContainer>
              <Title
                text={formatMessage({
                  defaultMessage: 'Highest-rated skills',
                  description: 'Section header for highest rated skills',
                })}
                size={TitleSize.XSMALL}
              />
              {highestRatedSkillsWithProgress.map((skill) => (
                <SupervisorSkillsCardRow key={skill.label} {...skill} isLoading={isLoadingProviderInsights} />
              ))}
            </SkillsSectionInfoContainer>
          </SkillsSectionContainer>
        )}

        {lowestRatedSkillsWithProgress.length > 0 && (
          <SkillsSectionContainer
            borderLeftColor={componentColors.warningSection.border}
            testID={tID('SupervisorSkillsCard-lowestRatedSkillsSection')}
          >
            <SkillsSectionInfoContainer>
              <Title
                text={formatMessage({
                  defaultMessage: 'Lowest-rated skills',
                  description: 'Section header for lowest rated skills',
                })}
                size={TitleSize.XSMALL}
              />
              {lowestRatedSkillsWithProgress.map((skill) => (
                <SupervisorSkillsCardRow key={skill.label} {...skill} isLoading={isLoadingProviderInsights} />
              ))}
            </SkillsSectionInfoContainer>
          </SkillsSectionContainer>
        )}

        {supervisorMetrics.length > 0 && (
          <MetricsContainer>
            {supervisorMetrics.map((metric) => (
              <InsightsMetricCard
                key={metric.testID}
                {...metric}
                metricsPerRow={supervisorMetricsPerRow}
                isLoading={isLoadingCareInsightsMetrics}
                showBorder
              />
            ))}
          </MetricsContainer>
        )}
      </BodyContainer>
    </CardContainer>
  )
}

export default SupervisorSkillsCard
